import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ArrowPathRoundedSquareIcon, UserGroupIcon } from "@heroicons/react/24/solid"
import { Moment } from "moment"
import { STATUSES } from "~/constants"
import { ReservationProps, SiteProps } from "../../../interfaces"
import classNames from "../../../utils/classNames"
import api2 from "../../../api2"
import { EditReservationContext } from "~/contexts/admin/EditReservationContext"
import { formatName } from "~/utils/formatName"
import IGroupedReservation from "../../../interfaces/IGroupedReservation"
import { CurrencyDollarIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid"
import { DateTime } from "luxon"
import twoDecimalPlaces from "../../../utils/twoDecimalPlaces"
import OrderSummary from "../Orders/OrderSummary"
import { useReactToPrint } from "react-to-print"
import EditInvoice from "../Invoices/EditInvoice"
import { serviceRateMode } from "~/api2/orders"
import ReservationInvoiceWrapper from "../../helpers/ReservationInvoiceWrapper"
import AutochargeConfirmationContent from "./AutochargeConfirmationContent"
import IPaymentMethods from "../../../interfaces/IPaymentMethods"
import { Summary } from "./EditReservationForm/Summary/Summary"
import { Guest } from "./EditReservationForm/Guests/Guest"
import { AdminContext } from "~/contexts/AdminContext"
import { usePreviews } from "./ReservationHooks/usePreviews"
import { useOrderInvoice } from "./ReservationHooks/useOrderInvoice"
import { useCamper } from "./ReservationHooks/useCamper"
import { useReservation } from "./ReservationHooks/useReservation"
import ReservationHistory from "./EditReservationForm/ReservationHistory/ReservationHistory"
import ReservationInvoice from "./EditReservationForm/ReservationInvoice/ReservationInvoice"
import IInvoice from "~/interfaces/IInvoice"
import { useDebouncedCallback } from "use-debounce"
import Button from "~/components/ui/Button"
import { useOrder } from "./ReservationHooks/useOrder"
import TermsAgreementModal from "~/components/TermsAgreement/TermsAgreementModal"
import moment from "moment"
import LongTermReservationModalContent from "../LongTermReservations/LongTermReservationModalContent"
import GroupModalContents from "../GroupedReservations/GroupModalContents"
import useWebsocket, { LiveWebAppNotification } from "~/hooks/useWebsocket"
import createSubscriptionLiveWebAppNotificationsChannel from "../../../channels/live_web_app_notifications_channel"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "~/constants/queryKeys"
import { GROUP_PAYMENT_TYPES } from "~/constants/group_payment_types"

interface ComponentProps {
  stripeAccountId: string
  reservation?: ReservationProps
  site?: SiteProps
  isProcessing: boolean
  currency: string
  campName: string
  startDate: Moment
  setStartDate: (v: Moment | null) => void
  endDate: Moment
  setEndDate: (v: Moment | null) => void
  openInvoiceModal: () => any
  onOpenDialogue: () => any
  setSlideoverOpen: (v: boolean) => void
  closeDrawer: () => void
}

const EditReservationForm = (props: ComponentProps) => {
  const {
    setSelectedReservation,
    camp,
    campingStyles,
    sites,
    reservationStates,
    showModal,
    closeModal,
    showConfirm,
    showNotification,
    selectedCamper,
    updateLocalReservation,
  } = useContext(AdminContext)
  const { errorMessage, showStaleMessage, setShowStaleMessage, isUpdatingReservation, setSelectedBookingIntervalId } =
    useContext(EditReservationContext)

  const isProcessing = props.isProcessing || isUpdatingReservation
  const site = sites?.find((s) => s.id === props.reservation?.site_id) || props.reservation?.site || props.site
  const campingStyle = campingStyles.find((cs) => cs.id === site?.camping_style_id)
  const id = props.reservation?.id
  const [groupedReservation, setGroupedReservation] = useState<IGroupedReservation>()
  const [orderPaymentMethods, setOrderPaymentMethods] = useState<IPaymentMethods[]>()
  const [license_plate, set_license_plate] = useState<string>()
  const [vehicle_year, set_vehicle_year] = useState<string>()
  const [vehicle_make, set_vehicle_make] = useState<string>()
  const [vehicle_color, set_vehicle_color] = useState<string>()
  const [vehicle_rig_type, set_vehicle_rig_type] = useState<string>()
  const [vehicle_rig_length, set_vehicle_rig_length] = useState<string>()
  const [vehicle_electric, set_vehicle_electric] = useState<string>()
  const [address, set_address] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [nextChargedInvoice, setNextChargedInvoice] = useState<IInvoice>()
  const passengers = useRef()
  const kid_campers = useRef()
  const pets_info = useRef()
  const targetRef = useRef(null)

  const [isSignModalOpen, setIsSignModalOpen] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState(moment(props.reservation?.tos_agreed_at).isValid())

  const handleAcceptAndSign = (tos_signature: string) => {
    setIsLoading(true)

    api2.Reservations.signTerms(props.reservation?.id as string, {
      tosSignature: tos_signature,
      tosAgreedAt: DateTime.now().setZone(camp.timezone).toISO() as string,
      lockVersion: props.reservation?.lock_version,
    })
      .then((response) => {
        updateLocalReservation({
          ...props.reservation,
          tos_signature: tos_signature,
          tos_agreed_at: DateTime.now().setZone(camp.timezone).toISO() as string,
        })
        setIsTermsAgreed(true)
        setIsLoading(false)
      })
      .catch((e) => {
        showNotification({
          type: "error",
          title: "Error updating Terms of Service",
          description: "Failed to update Terms of Service agreement. Please try again.",
        })
        setIsLoading(false)
      })
  }

  const previews = usePreviews({
    orderId: props.reservation?.order_id || "",
    campingStyleId: site?.camping_style_id,
    discount_type: props.reservation?.discount_type,
    serviceRate: serviceRateMode.payment_link,
    isPaid: props.reservation?.status === STATUSES.PAID,
    includedInvoice: true,
  })
  const { data: orderInvoice } = useOrderInvoice({ orderId: props.reservation?.order_id })
  const {
    data: orderData,
    refetch: refetchOrder,
    isLoading: orderIsLoading,
  } = useOrder({ orderId: props.reservation?.order_id })
  const {
    index: { refetch: refetchReservation },
  } = useReservation({
    reservationId: props.reservation?.id,
  })
  const usePrint = useReactToPrint({
    content: () => targetRef.current,
  })

  const camper = useCamper({
    camperId: props.reservation?.camper_id,
  })

  useEffect(() => {
    debouncedInvoice()

    if (previews?.data?.preview || orderInvoice?.order || camper?.data?.camper)
      setOrderPaymentMethods(() => {
        const orderPaymentMethods = [
          previews?.data?.preview?.payment_methods,
          orderInvoice?.order?.payment_methods,
          camper?.data?.camper?.payment_methods,
        ]
        return orderPaymentMethods.find((methods) => Array.isArray(methods) && methods.length > 0) ?? []
      })
  }, [previews.data?.preview, orderInvoice, camper.data?.camper])

  useEffect(() => {
    if (camper?.data?.camper) {
      const foundCamper = camper?.data.camper
      set_license_plate(foundCamper?.license_plate)
      set_vehicle_year(foundCamper?.vehicle_year)
      set_vehicle_make(foundCamper?.vehicle_make)
      set_vehicle_color(foundCamper?.vehicle_color)
      set_vehicle_rig_type(foundCamper?.vehicle_rig_type)
      set_vehicle_rig_length(foundCamper?.vehicle_rig_length)
      set_vehicle_electric(foundCamper?.vehicle_electric)
      set_address(foundCamper?.address)
    }
  }, [camper?.data])

  useWebsocket(
    (receivedNotification: LiveWebAppNotification) => {
      if (receivedNotification?.camp_id != camp.id) return false

      const receivedReservation = receivedNotification?.data?.reservation
      if (receivedNotification.type == "payment") {
        if (
          receivedReservation.status === STATUSES.PAID &&
          props.reservation?.order_id === receivedReservation?.order_id
        ) {
          refetchOrder()
          previews.refetch()
        }
      }
    },
    createSubscriptionLiveWebAppNotificationsChannel(),
    [camp?.id],
  )

  const debouncedInvoice = useDebouncedCallback(async () => {
    if (props.reservation?.long_term_reservation_id) {
      const { invoices } = await api2.LongTermReservations.nextChargedInvoice(
        props.reservation?.long_term_reservation_id,
      )
      setNextChargedInvoice(invoices[0])
    }
  }, 100)
  const queryClient = useQueryClient()

  const [total_owed, set_total_owed] = useState(
    twoDecimalPlaces((props.reservation?.total_owed_in_cents || Number(props.reservation?.total)) / 100),
  )
  const [total_paid, set_total_paid] = useState(twoDecimalPlaces(Number(props.reservation?.total_paid_in_cents) / 100))

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.RESERVATION_PREVIEWS],
      exact: true,
    })
    queryClient.invalidateQueries({
      queryKey: [queryKeys.ORDER],
      exact: true,
    })
    setTimeout(() => {
      previews?.refetch?.()
      refetchOrder()
    }, 500)
  }, [total_owed])

  const handleMarkAsPaid = async (callback?: () => void) => {
    setIsLoading(true)

    return api2.Reservations.update(props.reservation?.id as string, {
      total_paid_in_cents: parseFloat(total_owed) * 100.0,
      lock_version: props.reservation?.lock_version,
    })
      .then(({ reservation: updatedReservation }) => {
        set_total_paid(twoDecimalPlaces((updatedReservation.total_paid_in_cents || 0) / 100))
        showNotification({
          type: "success",
          title: "Saved",
          description: "Reservation has been successfully marked as paid",
        })
      })
      .catch((err) => {
        if (err.message && err.message.includes("Attempted to update a stale object")) {
          setShowStaleMessage(true)
        } else {
          showNotification({
            type: "error",
            title: "Error saving reservation",
            description: "Could not mark reservation as paid, please reload and try again",
          })
        }
      })
      .finally(() => {
        setIsLoading(false)
        callback()
      })
  }

  useEffect(() => {
    if (!props.reservation?.grouped_reservation_id) return

    api2.GroupedReservations.find(props.reservation?.grouped_reservation_id)
      .then((gr) => {
        setGroupedReservation(gr)
      })
      .catch((err) => {
        console.error(err)
      })
  }, [props.reservation?.grouped_reservation_id])

  useEffect(() => {
    set_total_owed(twoDecimalPlaces((props.reservation?.total_owed_in_cents || Number(props.reservation?.total)) / 100))
    set_total_paid(twoDecimalPlaces(Number(props.reservation?.total_paid_in_cents) / 100))
  }, [props.reservation?.total_owed_in_cents, props.reservation?.total_paid_in_cents])

  useEffect(() => {
    const startTime = DateTime.fromISO(props.startDate?.toISOString(), {
      zone: camp.timezone,
    }).toFormat("HH:mm")
    const endTime = DateTime.fromISO(props.endDate?.toISOString(), {
      zone: camp.timezone,
    }).toFormat("HH:mm")
    updateSelectedIntervalOption(startTime, endTime)
  }, [props.startDate, props.endDate])

  const updateSelectedIntervalOption = (sTime: string, eTime: string) => {
    const bookingIntervals = campingStyle?.booking_intervals
    const interval = bookingIntervals?.find((bi) => sTime === bi.start_time && eTime === bi.end_time)

    if (interval) {
      setSelectedBookingIntervalId(interval.id)
    } else {
      setSelectedBookingIntervalId(null)
    }
  }

  const handleClickStaleCta = () => {
    setShowStaleMessage(false)
    refetchReservation()
    props.closeDrawer()
  }

  const handleUpdateAutoCharge = useCallback(
    async (v: boolean) => {
      let isMakingRequest = true

      if (v) {
        if (showConfirm) {
          props.setSlideoverOpen(false)
          isMakingRequest = await showConfirm({
            title: "Enable autocharge for recurrences",
            description: (
              <AutochargeConfirmationContent
                order={previews.data?.order ?? orderInvoice?.order}
                invoice={previews.data?.invoice ?? orderInvoice?.invoice}
                nextChargedInvoice={nextChargedInvoice}
                paymentMethod={orderPaymentMethods?.[0]}
                camper={camper.data?.camper}
              />
            ),
            icon: CurrencyDollarIcon,
            onClose: () => props.setSlideoverOpen(true),
          })
        }
      }

      if (isMakingRequest) {
        api2.LongTermReservations.update(props.reservation?.long_term_reservation_id, {
          is_enabled_auto_charging: v,
        }).then(({ long_term_reservation }) => {
          const reservation = long_term_reservation.reservations.find((r) => r.id === props.reservation?.id)
          setSelectedReservation(reservation)
        })
      }
    },
    [
      showConfirm,
      nextChargedInvoice,
      orderPaymentMethods,
      camper.data?.camper,
      setSelectedReservation,
      props,
      previews.data?.order,
      orderInvoice?.order,
      previews.data?.invoice,
      orderInvoice?.invoice,
    ],
  )

  const history = useMemo(
    () => (
      <ReservationHistory
        reservation={props.reservation}
        orderId={props.reservation?.order_id}
        setSlideoverOpen={props.setSlideoverOpen}
      />
    ),
    [],
  )

  const taxRateLabel = useMemo(
    () => (campingStyle?.tax_rate ? campingStyle?.tax_rate_label : camp?.tax_rate ? camp?.tax_rate_label : "Tax"),
    [campingStyle, camp],
  )

  return (
    <div className="pb-5 pt-4">
      {errorMessage && (
        <div className="mb-4">
          <p className="text-sm sm:text-md text-red-600">{errorMessage}</p>
        </div>
      )}

      {showStaleMessage && (
        <div className="mb-4">
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon
                  className="mr-3 rounded-full p-2 h-9 w-9 bg-yellow-50 text-yellow-400"
                  aria-hidden="true"
                />
                <h3 className="text-base font-semibold text-gray-900">Stale reservation</h3>
              </div>
              <div className="mt-2 sm:flex sm:items-start sm:justify-between">
                <div className="max-w-xl text-base text-gray-500">
                  <p>
                    You attempted to update a reservation that has been modified in the meantime, please close this
                    reservation and re-open to fetch the latest data.
                  </p>
                </div>
                <div className="mt-5 sm:ml-6 sm:mt-0 sm:flex sm:shrink-0 sm:items-center">
                  <Button onClick={handleClickStaleCta} variant="white">
                    <span>Close</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <input hidden readOnly type="text" defaultValue={id} name="id" id="id" className="hidden" />
      <input
        hidden
        readOnly
        type="text"
        defaultValue={props.reservation?.camper_id}
        name="userId"
        id="userId"
        className="hidden"
      />
      <input
        hidden
        readOnly
        type="text"
        value={selectedCamper?.id || camper?.data?.camper?.id}
        name="camperId"
        id="camperId"
        className="hidden"
      />

      <>
        <div className="pb-1">
          {props.reservation?.grouped_reservation_id && (
            <div className="flex items-center bg-gray-100 justify-between mb-4 py-2 px-2.5 border border-dashed border-gray-300 rounded-md -mx-3">
              {/* TODO: Make this dynamic */}
              <div className="font-semibold">
                Part of{" "}
                {formatName(groupedReservation?.camper) == null
                  ? "a"
                  : " the " + formatName(groupedReservation?.camper)}{" "}
                group
              </div>
              <div className="flex">
                {props.reservation?.grouped_reservation_id && (
                  <a
                    className={classNames(
                      "focus:outline-none transition ease-in-out duration-100 rounded-md cursor-pointer",
                      "px-3.5 py-2.5",
                      "flex items-center text-sm sm:text-md bg-white hover:text-black hover:bg-gray-50 border border-gray-400 hover:border-gray-500",
                    )}
                    onClick={() => {
                      const groupModal = "group-modal"
                      showModal(
                        <GroupModalContents groupedReservationId={props.reservation?.grouped_reservation_id} />,
                        true,
                        "max-w-3xl",
                        {
                          closeCallback: () => {
                            closeModal(groupModal)
                          },
                          id: groupModal,
                        },
                      )
                    }}
                    data-testid="manage-group"
                  >
                    <UserGroupIcon className="w-5 h-5 text-gray-700 mr-2" />
                    <div>Manage Group</div>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {
          <div className="pb-1">
            {props.reservation?.long_term_reservation_id && (
              <div className="flex items-center bg-gray-100 justify-between mb-4 py-2 px-2.5 border border-dashed border-gray-300 rounded-md -mx-3">
                {/* TODO: Make this dynamic */}
                <div className="font-semibold">Part of a long term reservation</div>
                <div className="flex">
                  {props.reservation?.long_term_reservation_id && props.reservation?.id && (
                    <Button
                      className={classNames(
                        "focus:outline-none transition ease-in-out duration-100 rounded-md cursor-pointer",
                        "px-3.5 py-2.5",
                        "flex items-center text-sm sm:text-md bg-white hover:text-black hover:bg-gray-50 border border-gray-400 hover:border-gray-500",
                      )}
                      onClick={() => {
                        showModal(
                          <LongTermReservationModalContent reservation={props?.reservation} />,
                          false,
                          "max-w-3.5xl",
                        )
                      }}
                      data-testid="manage-long-term-reservation"
                    >
                      <ArrowPathRoundedSquareIcon className="w-5 h-5 text-gray-700 mr-2" />
                      <div>Manage Invoices</div>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        }

        <Summary
          setIsLoading={setIsLoading}
          handlePrint={usePrint}
          isLoading={isLoading}
          startDate={props.startDate}
          endDate={props.endDate}
          setStartDate={props.setStartDate}
          setEndDate={props.setEndDate}
          isProcessing={isProcessing}
          passengers={passengers}
          kid_campers={kid_campers}
          pets_info={pets_info}
          tos_agreed_at={props.reservation?.tos_agreed_at}
          setIsSignModalOpen={setIsSignModalOpen}
          isTermsAgreed={isTermsAgreed}
        />
      </>
      <Guest setSlideoverOpen={props.setSlideoverOpen} isLoading={isLoading} />
      {props.reservation?.status != STATUSES.BLOCKED &&
        props.reservation?.status != STATUSES.IMPORTED &&
        props.reservation?.status != STATUSES.BLOCKED_IMPORT && (
          <>
            <ReservationInvoice
              groupedReservation={groupedReservation}
              orderPaymentMethods={orderPaymentMethods}
              handleUpdateAutoCharge={handleUpdateAutoCharge}
              total_owed={total_owed}
              set_total_owed={set_total_owed}
              isProcessing={isProcessing}
              total_paid={total_paid}
              set_total_paid={set_total_paid}
              isLoading={isLoading}
              handleMarkAsPaid={handleMarkAsPaid}
              currency={camp.currency}
              setIsSlideoverOpen={props.setIsSlideoverOpen}
            />
            <OrderSummary
              order={orderData?.order?.status === STATUSES.PAID ? orderData?.order : previews?.data?.preview}
              isPreview={!(orderData?.order?.status === STATUSES.PAID)}
              isHidingServiceFee
              camp={camp}
              isLoading={previews?.isLoading || orderIsLoading || false}
              isIncludingLineItems
              isRecurring={!!props.reservation?.long_term_reservation_id}
              taxRateLabel={taxRateLabel}
            />
            {history}
          </>
        )}
      <TermsAgreementModal
        open={isSignModalOpen}
        onClose={() => setIsSignModalOpen(false)}
        campName={camp.name}
        rules={camp.rules}
        legalAcknowledgements={campingStyle?.legal_acknowledgements || []}
        setIsTermsAgreed={setIsTermsAgreed}
        tos_signature={props.reservation?.tos_signature}
        tos_agreed_at={props.reservation?.tos_agreed_at}
        handleAcceptAndSign={handleAcceptAndSign}
        isTermsAgreed={isTermsAgreed}
        isAdminView
        reservation={props.reservation}
        camp={camp}
      />
      <div className="hidden print:block" ref={targetRef}>
        <ReservationInvoiceWrapper reservation={props.reservation} order={props.reservation?.order}>
          {({ orderInvoice, setInvoice }) => {
            return (
              <EditInvoice
                invoice={orderInvoice}
                setSelectedInvoice={setInvoice}
                updateLocalInvoices={() => null}
                site={props.site}
                campingStyle={campingStyle}
                isProcessing={!!orderInvoice}
                customerView
                disabled
                isForPrint
                setIsSlideoverOpen={props.setSlideoverOpen}
              />
            )
          }}
        </ReservationInvoiceWrapper>
      </div>
    </div>
  )
}

export default EditReservationForm
